/**
 * Theme Manager
 * Handles manual theme toggling and persists the choice in localStorage.
 */

export function setupThemeToggle() {
    const themeBtn = document.getElementById('toggle-dark-mode');
    if (!themeBtn) {
        console.warn('Theme toggle button not found');
        return;
    }

    // Initialize theme based on preference, settings or system
    const hcieTheme = localStorage.getItem('hcie-theme');
    const settingsStr = localStorage.getItem('hcie_settings');
    let settingsTheme: string | null = null;
    
    try {
        const parsed = JSON.parse(settingsStr || '{}');
        const t = parsed?.interface?.theme;
        if (t && t !== 'system') settingsTheme = t;
        else if (t === 'system') {
            settingsTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
    } catch (_) {}

    // Priority: hcie_settings > hcie-theme > system preference
    const activeTheme = settingsTheme || hcieTheme
        || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

    document.documentElement.setAttribute('data-theme', activeTheme);
    localStorage.setItem('hcie-theme', activeTheme);  // Keep keys in sync
    updateThemeIcon(activeTheme);
    updateIconThemes(activeTheme);

    themeBtn.addEventListener('click', () => {
        const currentTheme = document.documentElement.getAttribute('data-theme');
        let newTheme: string;

        if (currentTheme === 'dark') {
            newTheme = 'light';
        } else if (currentTheme === 'light') {
            newTheme = 'dark';
        } else {
            // If currently automatic, we need to determine what it currently is
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            newTheme = isDark ? 'light' : 'dark';
        }

        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('hcie-theme', newTheme);
        
        // Also sync with hcie_settings if it exists
        try {
            const settingsStr = localStorage.getItem('hcie_settings');
            if (settingsStr) {
                const s = JSON.parse(settingsStr);
                if (s?.interface) {
                    s.interface.theme = newTheme;
                    localStorage.setItem('hcie_settings', JSON.stringify(s));
                }
            }
        } catch (_) {}

        updateThemeIcon(newTheme);
        updateIconThemes(newTheme);
        
        console.log(`Theme manually set to: ${newTheme}`);
    });

    // Listen for system theme changes to update icon if in auto mode
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (!localStorage.getItem('hcie-theme')) {
            const newTheme = e.matches ? 'dark' : 'light';
            updateThemeIcon(newTheme);
            updateIconThemes(newTheme);
        }
    });

    console.log('[THEME] Toggle initialized');
}

function updateThemeIcon(theme: string) {
    const themeBtn = document.getElementById('toggle-dark-mode');
    if (!themeBtn) return;

    if (theme === 'dark') {
        themeBtn.textContent = '🌙';
    } else {
        themeBtn.textContent = '☀️';
    }
}

/**
 * Updates all icon paths based on the active theme.
 */
function updateIconThemes(theme: string) {
    const isDark = theme === 'dark';
    const targetFolder = isDark ? 'ie_dark' : 'ie_color';
    const sourceFolder = isDark ? 'ie_color' : 'ie_dark';

    console.log(`[THEME] Updating icon paths to: ${targetFolder}`);

    const images = document.querySelectorAll('img');
    images.forEach(img => {
        const src = img.getAttribute('src');
        if (src && src.includes('resources/themes/')) {
            // Updated regex to handle leading './' and './public/' consistently
            // This ensures that swapped paths maintain their environment-specific prefixes.
            const newSrc = src.replace(/(.*)resources\/themes\/(ie_color|ie_dark)\//, `$1resources/themes/${targetFolder}/`);
            if (newSrc !== src) {
                img.setAttribute('src', newSrc);
            }
        }
    });
}

// Expose the internal updateIconThemes function for external access (e.g. from SettingsManager)
(window as any).updateIconThemes = updateIconThemes;
